import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import useProductsForOrder from "../../services/useProductsForOrder";
import { IProductForOrder } from "../../types/seederOrders";
import { useDebounce } from "../../hooks/useDebounce";

interface ProductSelectorProps {
  onAddProduct: (product: IProductForOrder, amount: number) => void;
  disabled?: boolean;
  getAvailableStock: (product: IProductForOrder) => number;
}

const ProductSelector = ({
  onAddProduct,
  disabled,
  getAvailableStock,
}: ProductSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const { data, isLoading } = useProductsForOrder({ search: debouncedSearch });
  const [amounts, setAmounts] = useState<{ [key: number]: number }>({});

  const handleAddProduct = (product: IProductForOrder) => {
    const amount = amounts[product.id] || 1;
    onAddProduct(product, amount);
    // Resetar quantidade após adicionar
    setAmounts({ ...amounts, [product.id]: 1 });
  };

  const filteredProducts = data?.results || [];

  const formatCurrency = (value: string) => {
    return parseFloat(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  if (disabled) {
    return (
      <Alert severity="info">
        Selecione uma parceira urbana antes de adicionar produtos.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Buscar produtos..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Grid container spacing={2}>
        {filteredProducts?.map((product) => {
          const availableStock = getAvailableStock(product);
          const isOutOfStock = availableStock === 0;
          const maxAmount = product.unlimited_stock
            ? undefined
            : availableStock;

          return (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {product.base_product_image && (
                  <CardMedia
                    component="img"
                    height="140"
                    image={product.base_product_image}
                    alt={product.base_product_name}
                    sx={{ objectFit: "cover" }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom noWrap>
                    {product.base_product_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Produtor: {product.producer_name}
                  </Typography>
                  <Box
                    sx={{ display: "flex", gap: 0.5, mb: 1, flexWrap: "wrap" }}
                  >
                    {product.categories.map((category, index) => (
                      <Chip key={index} label={category} size="small" />
                    ))}
                  </Box>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {formatCurrency(product.price)}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={isOutOfStock ? "error" : "text.secondary"}
                      fontWeight={isOutOfStock ? "bold" : "normal"}
                    >
                      Estoque disponível:{" "}
                      {product.unlimited_stock ? "Ilimitado" : availableStock}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <TextField
                      type="number"
                      size="small"
                      label="Qtd"
                      value={amounts[product.id] || 1}
                      onChange={(e) =>
                        setAmounts({
                          ...amounts,
                          [product.id]: Math.max(
                            1,
                            Math.min(
                              maxAmount || Infinity,
                              parseInt(e.target.value) || 1,
                            ),
                          ),
                        })
                      }
                      inputProps={{ min: 1, max: maxAmount }}
                      sx={{ width: 80 }}
                      disabled={isOutOfStock}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddProduct(product)}
                      fullWidth
                      disabled={isOutOfStock}
                    >
                      {isOutOfStock ? "Esgotado" : "Adicionar"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {filteredProducts?.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhum produto encontrado.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProductSelector;
